/** @jsx jsx */

import { useEffect } from 'react';
import { jsx, } from '@emotion/core';
import { Accordion, } from '@argo/base-patterns';
import { AccordionDisplayType, } from '@argo/base-patterns/accordion/types';
import {
  FlexRow,
  FlexCell,
} from '../../sew-common';
import ExpertReviewPanel from '../containers/ExpertReviewPanel';
import ExpertRatingPanel from '../containers/ExpertRatingPanel';
import ConsumerReviewPanel from '../containers/ConsumerReviewPanel';
import ConsumerRatingPanel from '../containers/ConsumerRatingPanel';
import AwardPanel from '../containers/AwardPanel';
import { Panels, } from './Constants';
import { convertStringToBoolean, } from '../../../utils';

type Props = {
  panel: string;
  isOpen: boolean;
  shouldEnableKBBLink: boolean;
  onExpertReviewsAccordionClick: (isOpen: boolean) => void;
  onExpertRatingsAccordionClick: (isOpen: boolean) => void;
  onConsumerReviewsAccordionClick: (isOpen: boolean) => void;
  onConsumerRatingsAccordionClick: (isOpen: boolean) => void;
  onAwardsAccordionClick: (isOpen: boolean) => void;
}

const ValidPanel = ({
  panel,
  isOpen,
  shouldEnableKBBLink,
  onExpertReviewsAccordionClick,
  onExpertRatingsAccordionClick,
  onConsumerReviewsAccordionClick,
  onConsumerRatingsAccordionClick,
  onAwardsAccordionClick,
}: Props,): JSX.Element => {
  // assign click event handler to accordion state
  const assignClickEventHandler = (panelType: string, accordionButton: Element,): void => {
    if (panelType === Panels.AWARDS) {
      onAwardsAccordionClick(!convertStringToBoolean(accordionButton?.getAttribute('aria-expanded',) || 'false',),);
    }
    if (panelType === Panels.EXPERT_RATINGS) {
      onExpertRatingsAccordionClick(!convertStringToBoolean(accordionButton?.getAttribute('aria-expanded',) || 'false',),);
    }
    if (panelType === Panels.EXPERT_REVIEWS) {
      onExpertReviewsAccordionClick(!convertStringToBoolean(accordionButton?.getAttribute('aria-expanded',) || 'false',),);
    }
    if (panelType === Panels.CONSUMER_RATINGS) {
      onConsumerRatingsAccordionClick(!convertStringToBoolean(accordionButton?.getAttribute('aria-expanded',) || 'false',),);
    }
    if (panelType === Panels.CONSUMER_REVIEWS) {
      onConsumerReviewsAccordionClick(!convertStringToBoolean(accordionButton?.getAttribute('aria-expanded',) || 'false',),);
    }
  };

  useEffect(() => {
    // query for accordion from DOM and assign click event handler to accordion state
    if (panel === Panels.EXPERT_RATINGS) {
      const accordionButton = document.querySelector('#expertRatingsAccordionButton',);
      if (accordionButton) { // conditionally add click event listener on first render
        accordionButton.addEventListener('click', () => {
          assignClickEventHandler(panel, accordionButton,);
        },);
      }
      return (): void => { // remove event listener on clean up
        if (accordionButton) {
          accordionButton.removeEventListener('click', () => { assignClickEventHandler(panel, accordionButton,); },);
        }
      };
    }
    if (panel === Panels.EXPERT_REVIEWS) {
      const accordionButton = document.querySelector('#expertReviewsAccordionButton',);
      if (accordionButton) { // conditionally add click event listener on first render
        accordionButton.addEventListener('click', () => {
          assignClickEventHandler(panel, accordionButton,);
        },);
      }
      return (): void => { // remove event listener on clean up
        if (accordionButton) {
          accordionButton.addEventListener('click', () => { assignClickEventHandler(panel, accordionButton,); },);
        }
      };
    }
    if (panel === Panels.CONSUMER_RATINGS) {
      const accordionButton = document.querySelector('#consumerRatingsAccordionButton',);
      if (accordionButton) { // conditionally add click event listener on first render
        accordionButton.addEventListener('click', () => {
          assignClickEventHandler(panel, accordionButton,);
        },);
      }
      return (): void => { // remove event listener on clean up
        if (accordionButton) {
          accordionButton.addEventListener('click', () => { assignClickEventHandler(panel, accordionButton,); },);
        }
      };
    }
    if (panel === Panels.CONSUMER_REVIEWS) {
      const accordionButton = document.querySelector('#consumerReviewsAccordionButton',);
      if (accordionButton) {
        accordionButton.addEventListener('click', () => {
          assignClickEventHandler(panel, accordionButton,);
        },);
      }
      return (): void => { // remove event listener on clean up
        if (accordionButton) {
          accordionButton.addEventListener('click', () => { assignClickEventHandler(panel, accordionButton,); },);
        }
      };
    }
    if (panel === Panels.AWARDS) {
      const accordionButton = document.querySelector('#awardsAccordionButton',);
      if (accordionButton) { // conditionally add click event listener on first render
        accordionButton.addEventListener('click', () => {
          assignClickEventHandler(panel, accordionButton,);
        },);
      }
      return (): void => { // remove event listener on clean up
        if (accordionButton) {
          accordionButton.addEventListener('click', () => { assignClickEventHandler(panel, accordionButton,); },);
        }
      };
    }
    return (): null => (null);
  }, [],);

  if (panel === Panels.EXPERT_REVIEWS) {
    return (
      <FlexRow key={Panels.EXPERT_REVIEWS}>
        <FlexCell>
          <Accordion
            isOpen={isOpen}
            accordionDisplayType={AccordionDisplayType.contained}
            accordionLabel="Expert Review"
            accordionButtonId="expertReviewsAccordionButton"
            accordionContentId="expertReviewsAccordionContent"
            rightLinkLabel="Clear"
            padding={{ paddingTop: 0, paddingRight: 12, paddingBottom: 0, paddingLeft: 12, }}
          >
            <ExpertReviewPanel />
          </Accordion>
        </FlexCell>
      </FlexRow>
    );
  }
  if (panel === Panels.EXPERT_RATINGS) {
    return (
      <FlexRow key={Panels.EXPERT_RATINGS}>
        <FlexCell>
          <Accordion
            isOpen={isOpen}
            accordionDisplayType={AccordionDisplayType.contained}
            accordionLabel="Expert Rating"
            accordionButtonId="expertRatingsAccordionButton"
            accordionContentId="expertRatingsAccordionContent"
            rightLinkLabel="Clear"
            padding={{ paddingTop: 0, paddingRight: 12, paddingBottom: 0, paddingLeft: 12, }}
          >
            <ExpertRatingPanel />
          </Accordion>
        </FlexCell>
      </FlexRow>
    );
  }
  if (panel === Panels.CONSUMER_REVIEWS) {
    return (
      <FlexRow key={Panels.CONSUMER_REVIEWS}>
        <FlexCell>
          <Accordion
            isOpen={isOpen}
            accordionDisplayType={AccordionDisplayType.contained}
            accordionLabel="Consumer Reviews"
            accordionButtonId="consumerReviewsAccordionButton"
            accordionContentId="consumerReviewsAccordionContent"
            rightLinkLabel="Clear"
            padding={{ paddingTop: 0, paddingRight: 12, paddingBottom: 0, paddingLeft: 12, }}
          >
            <ConsumerReviewPanel shouldEnableKBBLink={shouldEnableKBBLink} />
          </Accordion>
        </FlexCell>
      </FlexRow>
    );
  }
  if (panel === Panels.CONSUMER_RATINGS) {
    return (
      <FlexRow key={Panels.CONSUMER_RATINGS}>
        <FlexCell>
          <Accordion
            isOpen={isOpen}
            accordionDisplayType={AccordionDisplayType.contained}
            accordionLabel="Consumer Ratings"
            accordionButtonId="consumerRatingsAccordionButton"
            accordionContentId="consumerRatingsAccordionContent"
            rightLinkLabel="Clear"
            padding={{ paddingTop: 0, paddingRight: 12, paddingBottom: 0, paddingLeft: 12, }}
          >
            <ConsumerRatingPanel />
          </Accordion>
        </FlexCell>
      </FlexRow>
    );
  }
  if (panel === Panels.AWARDS) {
    return (
      <FlexRow key={Panels.AWARDS}>
        <FlexCell>
          <Accordion
            isOpen={isOpen}
            accordionDisplayType={AccordionDisplayType.contained}
            accordionLabel="Awards"
            accordionButtonId="awardsAccordionButton"
            accordionContentId="awardsAccordionContent"
            rightLinkLabel="Clear"
            padding={{ paddingTop: 0, paddingRight: 12, paddingBottom: 0, paddingLeft: 12, }}
          >
            <AwardPanel />
          </Accordion>
        </FlexCell>
      </FlexRow>
    );
  }
  return <div />;
};

export default ValidPanel;